(() => {
  if (customElements.get('hedera-cart-discount')) return;
  const widgets = () => [...document.querySelectorAll('hedera-cart-discount')];
  const root = () => window.Shopify?.routes?.root || '/';
  const key = code => String(code).trim().toLowerCase();
  let busy = false;
  let initialRequest;
  let revision = 0;

  async function readCart() {
    const response = await fetch(`${root()}cart.js`, { cache: 'no-store' });
    if (!response.ok) throw new Error('cart');
    return response.json();
  }

  function codesIn(cart) {
    const codes = new Map();
    const put = (code, applicable) => {
      if (code) codes.set(key(code), { code: String(code), applicable });
    };
    (cart.discount_codes || []).forEach(d => put(d.code, d.applicable));
    const applications = [...(cart.cart_level_discount_applications || [])];
    (cart.items || []).forEach(item => {
      (item.line_level_discount_allocations || []).forEach(d => applications.push(d.discount_application));
    });
    applications.forEach(d => {
      if (d?.type === 'discount_code') put(d.title, true);
    });
    return [...codes.values()];
  }

  function paint(cart) {
    widgets().forEach(widget => {
      const list = widget.querySelector('[data-discount-codes]');
      list.replaceChildren();
      codesIn(cart).forEach(discount => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'hd-discount__remove';
        button.dataset.discountRemove = discount.code;
        button.textContent = `${discount.code} ×`;
        button.setAttribute('aria-label', `${widget.dataset.remove}: ${discount.code}`);
        list.appendChild(button);
      });
    });
  }

  function sectionTargets() {
    const page = document.querySelector('cart-items');
    if (page) return page.getSectionsToRender().map(s => ({ id: s.id, section: s.section, selector: s.selector }));
    const drawer = document.querySelector('cart-notification');
    return drawer ? drawer.getSectionsToRender().map(s => ({ id: s.id, section: s.id, selector: '.shopify-section' })) : [];
  }

  function renderSections(cart, targets) {
    const changes = targets.map(target => {
      const container = document.getElementById(target.id);
      const source = cart.sections?.[target.section];
      if (!container || typeof source !== 'string') throw new Error('sections');
      const parsed = new DOMParser().parseFromString(source, 'text/html').querySelector(target.selector);
      if (!parsed) throw new Error('sections');
      return [container.querySelector(target.selector) || container, parsed.innerHTML];
    });
    changes.forEach(([node, html]) => { node.innerHTML = html; });
    document.querySelector('cart-notification')?.syncFreeShippingProgress();
  }

  function lock() {
    const nodes = [...document.querySelectorAll('cart-items, #cart-notification-product, .cart__ctas, .cart--checkout__button, product-form, #hedera-cart-complementary-source')];
    const previous = nodes.map(node => [node, node.inert]);
    nodes.forEach(node => { node.inert = true; });
    widgets().forEach(widget => {
      widget.setAttribute('aria-busy', 'true');
      widget.querySelectorAll('button, input').forEach(node => { node.disabled = true; });
    });
    return () => {
      previous.forEach(([node, inert]) => { node.inert = inert; });
      widgets().forEach(widget => {
        widget.removeAttribute('aria-busy');
        widget.querySelectorAll('button, input').forEach(node => { node.disabled = false; });
      });
    };
  }

  class HederaCartDiscount extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.addEventListener('click', event => {
        const remove = event.target.closest('[data-discount-remove]');
        if (remove) this.update(remove.dataset.discountRemove, true);
        else if (event.target.closest('[data-discount-apply]')) this.update();
      });
      this.addEventListener('keydown', event => {
        if (event.key === 'Enter' && event.target.matches('[data-discount-input]')) {
          event.preventDefault();
          this.update();
        }
      });
      const version = revision;
      if (!initialRequest) initialRequest = readCart();
      initialRequest.then(cart => { if (!busy && version === revision) paint(cart); }).catch(() => {});
    }

    message(name, error = false) {
      const region = this.querySelector('[data-discount-message]');
      region.textContent = this.dataset[name] || '';
      region.classList.toggle('hd-discount__error', error);
      this.querySelector('[data-discount-input]').setAttribute('aria-invalid', String(error));
    }

    async update(removeCode, remove = false) {
      if (busy) return;
      const input = this.querySelector('[data-discount-input]');
      const code = remove ? removeCode : input.value.trim();
      if (!code) { this.message('empty', true); input.focus(); return; }
      if (document.querySelector('cart-notification[aria-busy], .cart__items--disabled, product-form button.loading')) {
        this.message('wait'); return;
      }
      busy = true;
      revision++;
      const unlock = lock();
      let updated = false;
      this.message('wait');
      try {
        const before = await readCart();
        const codes = codesIn(before).filter(d => key(d.code) !== key(code)).map(d => d.code);
        if (!remove) codes.push(code);
        const targets = sectionTargets();
        const response = await fetch(`${root()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: codes.join(','), sections: targets.map(s => s.section), sections_url: location.pathname })
        });
        if (!response.ok) throw new Error('update');
        const cart = await response.json();
        if (!Array.isArray(cart.items) || !Number.isFinite(cart.total_price)) throw new Error('cart');
        updated = true;
        initialRequest = Promise.resolve(cart);
        paint(cart);
        renderSections(cart, targets);
        const result = codesIn(cart).find(d => key(d.code) === key(code));
        if (remove) {
          this.message(result ? 'error' : 'removed', !!result);
        } else if (result?.applicable === true) {
          this.message('applied'); input.value = '';
        } else if (result?.applicable === false) {
          this.message('invalid', true);
        } else {
          this.message('checkout');
        }
        document.dispatchEvent(new CustomEvent('hedera:cart:discount-updated', { detail: { cart } }));
      } catch (_) {
        this.message(updated ? 'refresh' : 'error', true);
      } finally {
        busy = false;
        unlock();
      }
    }
  }
  customElements.define('hedera-cart-discount', HederaCartDiscount);
})();
